import { isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * <gamba-newsletter-popup> — el popup de newsletter de la home.
 *
 * El diálogo es un `<dialog>` nativo abierto con `showModal()`, así que del
 * contrato de accesibilidad de un modal el motor cubre cuatro cosas sin código
 * nuestro: la trampa de foco, el fondo inerte (no tabulable, no clickeable, no
 * leído), el cierre con `Escape` y el top layer. El por qué está en el
 * comentario de cabecera de `sections/gamba-newsletter-popup.liquid`, y es el
 * mismo patrón de `assets/gamba-menu-drawer.js`.
 *
 * Lo que queda para este archivo son las seis cosas que el motor NO hace:
 *
 *   1. Abrirlo solo a los N segundos (3 por diseño, medido en el prototipo).
 *   2. NO abrirlo encima de otra superficie abierta, ni robarle el foco a
 *      alguien que está tipeando — y reintentar en vez de rendirse.
 *   3. Recordar que ya se descartó o que ya se dio de alta, para que no vuelva
 *      en cada carga — y respetar el gate de `data-subscribed`, que es el mismo
 *      "ya está adentro" pero leído de la sesión del cliente y no del storage.
 *   4. Reabrirlo cuando la página vuelve del alta (el form no es AJAX), para
 *      que el mensaje de confirmación se vea.
 *   5. Bloquear el scroll de la página de atrás.
 *   6. Cerrar al click en el velo y devolver el foco al cerrar.
 *
 * NO extiende `Component` (`assets/component.js`) a propósito, igual que
 * `gamba-menu-drawer.js` y `gamba-rail.js`: de esa clase base sólo se usarían
 * `refs` y los bindings `on:`, y a cambio vendría un MutationObserver con
 * `subtree: true` por instancia.
 */

/** El descarte: sello de tiempo, con ventana configurable por setting. */
const KEY_DISMISSED = 'gamba:newsletter-popup:dismissed';
/** El alta: no vence nunca. Quien se suscribió no tiene que volver a verlo. */
const KEY_SUBSCRIBED = 'gamba:newsletter-popup:subscribed';

const DAY_MS = 86400000;
/** Cada cuánto se reintenta cuando hay otra superficie abierta. */
const RETRY_MS = 1000;
/** Cuántas veces. 30 x 1s = medio minuto y se rinde para esta carga. */
const RETRY_MAX = 30;

/**
 * El foco NO se le roba a nadie que esté escribiendo.
 * `[contenteditable]` a secas también matchea `contenteditable="false"`, así
 * que se listan los dos valores que sí editan.
 */
const TYPING = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/**
 * Lee una clave de `localStorage`.
 *
 * EL `try` ARRANCA ANTES DE TOCAR LA PROPIEDAD, no alrededor del `getItem`: en
 * Safari en navegación privada vieja y en un browser con los datos de sitio
 * bloqueados, el accessor `window.localStorage` MISMO tira. Envolver sólo la
 * llamada no alcanza.
 *
 * Y cuando falla devuelve `null`, o sea "nunca se descartó": el popup aparece y
 * lo único que se pierde es la memoria. Tratar el error como "ya se descartó"
 * dejaría el popup invisible para siempre en esos browsers, que es un fallo
 * silencioso mucho peor que uno visible.
 *
 * @param {string} key - La clave.
 * @returns {string | null} El valor, o `null` si no está o si el storage falló.
 */
function readStore(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Escribe una clave en `localStorage`. Si falla, no pasa nada: el popup sigue
 * funcionando, sólo no se acuerda. En ningún caso el error escapa — la home no
 * puede romperse por un popup.
 *
 * @param {string} key - La clave.
 * @param {string} value - El valor.
 */
function writeStore(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Sin memoria del descarte, pero sin romper nada.
  }
}

class GambaNewsletterPopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {number} */
  #timer = 0;

  /** @type {number} */
  #retries = 0;

  /** El elemento que tenía el foco antes de abrir. @type {HTMLElement | null} */
  #origin = null;

  /** En el editor de theme no se autoabre ni se persiste nada. @type {boolean} */
  #designMode = Boolean(window.Shopify && window.Shopify.designMode);

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#dialog = dialog;

    // El click en el velo llega con `event.target === dialog`: el `::backdrop`
    // no es un nodo propio. `isClickedOutside` de Horizon tiene el caso especial
    // (`assets/utilities.js:396-398`: para un target HTMLDialogElement compara
    // el punto contra el rect en vez de usar `contains`, que diría que sí
    // siempre).
    dialog.addEventListener('click', this.#onVeilClick);
    // Un solo lugar para el desarmado, y por eso se escucha `close` y no se hace
    // el trabajo en un método `close()`: `Escape` cierra el diálogo por spec sin
    // pasar por ningún handler nuestro. Colgado de `close`, el desbloqueo del
    // scroll y el registro del descarte no se pueden desincronizar por una vía
    // de cierre que no hayamos previsto.
    dialog.addEventListener('close', this.#onClose);

    for (const button of this.querySelectorAll('[data-popup-close]')) {
      button.addEventListener('click', this.#close);
    }

    if (this.#designMode) {
      // En el editor el popup se abre cuando el cliente selecciona la sección,
      // y NO por temporizador: un popup que se abre solo cada vez que se
      // recarga la vista previa hace imposible editar el resto de la home. Y no
      // se lee ni se escribe storage, así que la vista previa nunca queda
      // "descartada".
      document.addEventListener('shopify:section:select', this.#onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect);
      return;
    }

    // ¿La página volvió de un POST del formulario? El snippet compartido
    // renderiza `.gamba-nl__message` para el alta y para el error, y el form no
    // es AJAX: sin esto el visitante habría llenado el campo y no vería nada.
    if (this.#message) {
      this.#openNow();
      return;
    }

    // Quien tiene sesión iniciada y ya acepta marketing no lo ve nunca: la
    // sección emite `data-subscribed` desde `customer.accepts_marketing`. Va
    // DESPUÉS del `#message` a propósito — el caso "se acaba de suscribir con
    // la sesión abierta" tiene que ver su confirmación, y para ese request el
    // atributo ya está puesto.
    if (this.hasAttribute('data-subscribed')) return;

    if (this.#dismissed) return;

    this.#timer = window.setTimeout(this.#tryOpen, this.delay);
  }

  disconnectedCallback() {
    const dialog = this.#dialog;

    window.clearTimeout(this.#timer);
    this.#timer = 0;

    if (dialog) {
      // Si la sección se re-renderiza (editor de theme) con el popup abierto, el
      // nodo se va pero `html[scroll-lock]` se queda: la página quedaría sin
      // scroll y sin nada que la desbloquee. Horizon hace lo mismo en
      // `assets/theme-drawer.js:90-91`.
      unlockScroll(dialog);
      dialog.removeEventListener('click', this.#onVeilClick);
      dialog.removeEventListener('close', this.#onClose);
    }

    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#onSectionDeselect);
  }

  /**
   * La demora al entrar, en milisegundos. La sección la emite ya multiplicada.
   *
   * @returns {number}
   */
  get delay() {
    const value = Number(this.dataset.delay);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * Días hasta que vuelve a aparecerle a quien lo cerró. `0` = no vuelve nunca.
   *
   * @returns {number}
   */
  get reappearDays() {
    const value = Number(this.dataset.reappearDays);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * El mensaje de vuelta del formulario, si la página viene de un POST.
   *
   * ACOPLAMIENTO DECLARADO: la clase es del snippet compartido
   * `gamba-newsletter-form.liquid`, no de este componente. Se elige mirar el DOM
   * y no un flag de Liquid porque `form.posted_successfully?` sólo existe
   * ADENTRO del bloque del formulario, y acá hace falta afuera.
   *
   * @returns {HTMLElement | null}
   */
  get #message() {
    const message = this.querySelector('.gamba-nl__message');
    return message instanceof HTMLElement ? message : null;
  }

  /**
   * Si el popup no tiene que aparecer porque ya se descartó o porque el
   * visitante se suscribió.
   *
   * @returns {boolean}
   */
  get #dismissed() {
    if (readStore(KEY_SUBSCRIBED)) return true;

    const raw = readStore(KEY_DISMISSED);
    if (!raw) return false;

    const at = Number(raw);
    // Valor basura (otra versión del theme, una extensión, un `1` viejo): se
    // trata como si no se hubiera descartado nunca, que es el default seguro.
    if (!Number.isFinite(at) || at <= 0) return false;

    const days = this.reappearDays;
    if (days <= 0) return true;

    return Date.now() - at < days * DAY_MS;
  }

  /**
   * Las cuatro superficies encima de las que el popup NO se abre.
   *
   * @returns {boolean}
   */
  #blocked() {
    // 1. Otro `<dialog>` abierto: el cart drawer (`#cart-drawer`) y el drawer de
    //    menú (`#gamba-menu-drawer`). `showModal()` sobre un diálogo ya abierto
    //    apila dos en el top layer, y ahí `Escape` cierra el de arriba: el
    //    visitante que quería cerrar el carrito cierra el popup y el carrito
    //    sigue ahí.
    const other = document.querySelector('dialog[open]');
    if (other && other !== this.#dialog) return true;

    // 2. El scroll tomado por cualquier cosa. Red de seguridad para cualquier
    //    overlay que no sea `<dialog>`: `lockScroll()` pone `html[scroll-lock]`
    //    (`assets/utilities.js:356`) y lo usan las superficies modales del theme.
    if (document.documentElement.hasAttribute('scroll-lock')) return true;

    // 3. El panel de búsqueda, que NO es `<dialog>` y NO toma el scroll, así que
    //    los dos gates de arriba no lo ven. Lo abre y lo cierra moviendo
    //    `hidden` (`assets/gamba-search-panel.js:168` y `:203`).
    const search = document.getElementById('gamba-search-panel');
    if (search instanceof HTMLElement && !search.hidden) return true;

    // 4. Alguien está escribiendo. Robarle el foco pierde lo escrito.
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body && active.matches(TYPING)) return true;

    return false;
  }

  /**
   * Vencida la demora: si alguna superficie está abierta NO se descarta —eso
   * dejaría el popup sin aparecer nunca por haber tenido el carrito abierto al
   * segundo 3—, se reintenta. Agotados los reintentos se rinde para esta carga,
   * sin escribir nada: la próxima visita lo intenta de nuevo.
   */
  #tryOpen = () => {
    this.#timer = 0;

    if (this.#blocked()) {
      if (this.#retries >= RETRY_MAX) return;
      this.#retries += 1;
      this.#timer = window.setTimeout(this.#tryOpen, RETRY_MS);
      return;
    }

    this.#open();
  };

  /**
   * Apertura inmediata al volver de un POST del formulario. Si el mensaje es de
   * alta (y no de error) se anota la suscripción, que no vence.
   */
  #openNow() {
    const message = this.#message;

    if (message && !message.classList.contains('gamba-nl__message--error')) {
      writeStore(KEY_SUBSCRIBED, '1');
    }

    this.#open();
  }

  #open() {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;

    // El popup se abre SOLO, así que el "elemento que lo abrió" suele ser el
    // `<body>`. Se guarda igual: si el visitante estaba enfocando algo, ahí
    // vuelve al cerrar.
    this.#origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Antes de `showModal()`: el diálogo entra en el top layer y el
    // `html[scroll-lock]` de `assets/base.css:36-39` tiene que estar puesto para
    // ese primer fotograma, o la página de atrás salta. La canaleta que evita el
    // corrimiento la reserva `snippets/gamba-cart-drawer-styles.liquid:36-82`.
    lockScroll(dialog);
    dialog.showModal();

    // El `autofocus` del markup manda el foco al ✕, que es lo que corresponde en
    // un diálogo que se abre solo. La excepción es cuando hay un mensaje de
    // vuelta del formulario: ahí el foco va al mensaje, para que el lector de
    // pantalla anuncie el alta o el error en vez de "cerrar".
    this.#message?.focus();
  }

  #close = () => {
    this.#dialog?.close();
  };

  #onClose = () => {
    const dialog = this.#dialog;
    if (!dialog) return;

    // `unlockScroll` lleva un set de dueños con conteo: si otra superficie
    // también tiene el scroll tomado, esto no se lo suelta.
    unlockScroll(dialog);

    if (!this.#designMode) writeStore(KEY_DISMISSED, String(Date.now()));

    // El `<dialog>` ya devuelve el foco al elemento que lo tenía antes de abrir,
    // pero acá se sabe cuál TIENE que quedar enfocado, así que se afirma. Se
    // saltea cuando ese elemento ya no está renderizado: enfocar un elemento
    // escondido no hace nada y deja el foco en el `<body>` de todas formas.
    const origin = this.#origin;
    this.#origin = null;
    if (origin?.isConnected && origin.offsetParent !== null) origin.focus();
  };

  /** @param {MouseEvent} event */
  #onVeilClick = (event) => {
    const dialog = this.#dialog;
    if (dialog && isClickedOutside(event, dialog)) this.#close();
  };

  /** @param {Event} event */
  #onSectionSelect = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.#open();
  };

  /** @param {Event} event */
  #onSectionDeselect = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.#close();
  };
}

if (!customElements.get('gamba-newsletter-popup')) {
  customElements.define('gamba-newsletter-popup', GambaNewsletterPopup);
}
